/**
 * FieldFilterModal (Unit 5 Phase 2) — multi-condition filter editor.
 *
 * SIBLING OF FieldSortModal, not a fork of it. Same own chrome (no ModalShell,
 * for the reason FieldColumnsModal.jsx:4-9 records), same three bands, same
 * root --z-modal, same working-copy contract:
 *
 *   copy on open  →  edit the draft  →  Apply commits  →  Cancel/✕ discards
 *
 * CLEAR WIPES THE DRAFT, NOT THE STORED FILTER, and does not close — matching
 * the Sort modal deliberately. Two editors in one unit behaving differently
 * about the same word would be worse than either behaviour on its own. The one
 * place a clear IS a commit is the grid's zero-result "Clear filters", which
 * has no draft and no Apply to follow it.
 *
 * THE DRAFT IS TWO PIECES — conditions and logic — because they commit as one
 * decision (useFieldFilterState:20-24). Apply hands both up together.
 *
 * AND/OR ROW renders only at >= 2 conditions, Bob's renderFilterModal:9407.
 * With one condition there is nothing to combine and the control would be a
 * question with no meaning.
 *
 * VALUE CONTROL BY KIND:
 *   text   → text input
 *   number → number input
 *   enum   → BottomSheetSelect, single-select, options from the values
 *            actually present in the loaded records (getFieldUniqueValues)
 *   bool   → NOTHING. Both its operators are self-contained.
 * and suppressed entirely for any valueless operator (Bob's
 * operatorNeedsValue:9237-9239).
 *
 * ⚠ BottomSheetSelect IS USED AS-IS, single-select. It is not extended here,
 * which is also why there are no 'is any of' / 'is none of' operators — see
 * filterRecords.js's OPERATORS_BY_KIND for that decision in full.
 *
 * ITS OPEN STATE IS THE ELEVENTH SENTINEL, tmgFieldFilterSheet, owned by the
 * host — NOT local state in this component. The same component in the same
 * role is already router-native in the record modal (tmgFieldSheetField,
 * FieldRecordModal openSheet), and the reason is ANDROID HARDWARE BACK, which
 * is what the whole zone's history-based architecture exists for
 * (FieldZonePlaceholder:267-272). Held locally, a back gesture inside the
 * value picker finds nothing of its own to pop and takes tmgFieldFilterOpen
 * instead — closing this modal and discarding the rep's draft. Because the
 * push SPREADS state, tmgFieldFilterOpen rides onto the pushed entry, this
 * component never unmounts while the picker is up, and the working copy below
 * survives the round trip untouched.
 *
 * RESET RULES, both of them load-bearing:
 *   changing the FIELD    → operator resets to that kind's first, value clears
 *   changing the OPERATOR → value clears if the new operator takes none
 * Without the first, a `>` left over from a number field rides onto a text
 * field and sanitizeFilter silently drops the whole condition on the next
 * read. Without the second, a stale value sits behind an `is empty` and
 * reappears if the rep switches back.
 */

import React, { useEffect, useState } from 'react';
import FieldPickerOptions, { firstPickerKey } from './FieldPickerOptions';
import BottomSheetSelect from './BottomSheetSelect';
import {
  operatorsForKey, operatorNeedsValue, getFieldUniqueValues,
} from '../data/filterRecords';
import { KIND_BY_KEY } from '../data/fieldValueRules';
import './FieldFilterModal.css';

/* Stable empty defaults, module scope and never mutated — same reasoning as
   FieldSortModal's, and FieldRecordsList's before that. */
const EMPTY_FIELDS = [];
const EMPTY_RECORDS = [];
const EMPTY_HIDDEN = new Set();

/** A new condition seeds with the field's first operator and no value. */
function newCondition(fieldKey) {
  return { fieldKey, operator: operatorsForKey(fieldKey)[0], value: '' };
}

export default function FieldFilterModal({
  fields = EMPTY_FIELDS,
  hidden = EMPTY_HIDDEN,
  records = EMPTY_RECORDS,
  filter,
  filterLogic,
  sheetIndex = null,
  onSheetOpen,
  onSheetClose,
  onApply,
  onClose,
}) {
  // THE WORKING COPY. Conditions are cloned, not aliased — editing a value
  // must not mutate the committed array the grid is still rendering from.
  const [draft, setDraft] = useState(
    () => (Array.isArray(filter) ? filter.map((c) => ({ ...c })) : []),
  );
  const [logic, setLogic] = useState(() => (filterLogic === 'OR' ? 'OR' : 'AND'));

  const firstKey = firstPickerKey(fields, hidden);

  // Escape (desktop) — capture + stopPropagation, this modal only. See the
  // matching note in FieldSortModal: FieldViewPanel's listener is gated off
  // while an editor is up, so each editor owns its own key.
  //
  // THE SHEET WINS WHEN IT IS OPEN. BottomSheetSelect registers its own
  // capture listener and closes itself; this one steps aside rather than also
  // firing, because two handlers on `window` in the same phase both run and
  // the rep would lose the whole draft to dismiss one value picker. The
  // predicate is now the SENTINEL index rather than local state — same rule,
  // one source of truth for whether the picker is up.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      if (sheetIndex != null) return; // the sheet owns this keypress
      e.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [sheetIndex, onClose]);

  const setCondition = (index, patch) => {
    setDraft((prev) => prev.map((c, i) => (i === index ? { ...c, ...patch } : c)));
  };

  // Field change resets the operator and clears the value — the new kind's
  // operator set may not contain the old operator at all.
  const changeField = (index, fieldKey) => {
    setDraft((prev) => prev.map((c, i) => (i === index ? newCondition(fieldKey) : c)));
  };

  // Operator change keeps the value only if the new operator takes one.
  const changeOperator = (index, operator) => {
    setDraft((prev) => prev.map((c, i) => (i === index
      ? { ...c, operator, value: operatorNeedsValue(operator) ? c.value : '' }
      : c)));
  };

  const removeCondition = (index) => {
    setDraft((prev) => prev.filter((_, i) => i !== index));
  };

  const addCondition = () => {
    if (!firstKey) return;
    setDraft((prev) => [...prev, newCondition(firstKey)]);
  };

  // Draft-only, matching FieldSortModal's handleClear. Logic goes back to the
  // default too — leaving 'OR' behind an empty list would silently apply to
  // whatever the rep adds next.
  const handleClear = () => {
    setDraft([]);
    setLogic('AND');
  };

  // The host owns the close: its handler commits and then pops the sentinel,
  // so Apply resolves through the SAME single navigate(-1) every other close
  // path does. This component never navigates.
  const handleApply = () => onApply(draft, logic);

  const sheetCondition = sheetIndex == null ? null : draft[sheetIndex];

  return (
    <div
      className="ffm"
      data-testid="field-filter-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Filter"
    >
      <div className="ffm-header">
        <h2 className="ffm-title">Filter</h2>
        <button
          type="button"
          className="ffm-bulk"
          data-testid="field-filter-clear"
          onClick={handleClear}
          disabled={draft.length === 0}
        >
          Clear
        </button>
        <button
          type="button"
          className="ffm-close"
          data-testid="field-filter-close"
          onClick={onClose}
          aria-label="Close filter"
        >
          ✕
        </button>
      </div>

      <div className="ffm-body">
        {/* Bob's :9407 — only meaningful once there are two things to combine. */}
        {draft.length >= 2 && (
          <div className="ffm-logic" data-testid="field-filter-logic">
            <span className="ffm-logic-label">Match</span>
            <div className="ffm-logic-toggle">
              {['AND', 'OR'].map((option) => (
                <button
                  key={option}
                  type="button"
                  className={`ffm-logic-btn${logic === option ? ' ffm-logic-btn-active' : ''}`}
                  data-testid={`field-filter-logic-${option.toLowerCase()}`}
                  aria-pressed={logic === option}
                  onClick={() => setLogic(option)}
                >
                  {option}
                </button>
              ))}
            </div>
            <span className="ffm-logic-label">of the following</span>
          </div>
        )}

        {draft.length === 0 && (
          <p className="ffm-empty" data-testid="field-filter-empty">
            No conditions yet. Every record is showing.
          </p>
        )}

        {draft.map((cond, index) => {
          const kind = KIND_BY_KEY.get(cond.fieldKey);
          const operators = operatorsForKey(cond.fieldKey);
          const needsValue = operatorNeedsValue(cond.operator);
          return (
            /* Keyed on the INDEX, deliberately — same reasoning as
               FieldSortModal:142-146: a condition has no identity of its own,
               two may name the same field, and every control here is driven
               from `draft` so no uncontrolled state rides on the key. */
            <div className="ffm-cond" key={index} data-testid={`field-filter-cond-${index}`}>
              <div className="ffm-cond-top">
                <select
                  className="ffm-select"
                  data-testid={`field-filter-field-${index}`}
                  aria-label={`Condition ${index + 1} field`}
                  value={cond.fieldKey}
                  onChange={(e) => changeField(index, e.target.value)}
                >
                  <FieldPickerOptions
                    fields={fields}
                    hidden={hidden}
                    value={cond.fieldKey}
                  />
                </select>
                <button
                  type="button"
                  className="ffm-remove"
                  data-testid={`field-filter-remove-${index}`}
                  onClick={() => removeCondition(index)}
                  aria-label={`Remove condition ${index + 1}`}
                >
                  ✕
                </button>
              </div>

              <div className="ffm-cond-bottom">
                <select
                  className="ffm-select ffm-select-op"
                  data-testid={`field-filter-op-${index}`}
                  aria-label={`Condition ${index + 1} operator`}
                  value={cond.operator}
                  onChange={(e) => changeOperator(index, e.target.value)}
                >
                  {operators.map((op) => (
                    <option key={op} value={op}>{op}</option>
                  ))}
                </select>

                {/* bool renders no value control at all — both its operators
                    are self-contained — and neither does any valueless
                    operator on any kind. */}
                {needsValue && kind === 'enum' && (
                  <button
                    type="button"
                    className="ffm-value ffm-value-picker"
                    data-testid={`field-filter-value-${index}`}
                    onClick={() => onSheetOpen(index)}
                  >
                    {cond.value === '' ? 'Choose…' : cond.value}
                  </button>
                )}
                {needsValue && kind === 'number' && (
                  <input
                    type="number"
                    className="ffm-value"
                    data-testid={`field-filter-value-${index}`}
                    aria-label={`Condition ${index + 1} value`}
                    value={cond.value}
                    onChange={(e) => setCondition(index, { value: e.target.value })}
                  />
                )}
                {needsValue && kind !== 'enum' && kind !== 'number' && (
                  /* text AND the six no-kind read-only rows, which resolve to
                     the text operator set and take a text value. */
                  <input
                    type="text"
                    className="ffm-value"
                    data-testid={`field-filter-value-${index}`}
                    aria-label={`Condition ${index + 1} value`}
                    value={cond.value}
                    onChange={(e) => setCondition(index, { value: e.target.value })}
                  />
                )}
              </div>
            </div>
          );
        })}

        {/* NO HANDLER, NO CONTROL, applied to a data precondition: with no
            offerable field there is nothing a condition could name. */}
        {firstKey && (
          <button
            type="button"
            className="ffm-add"
            data-testid="field-filter-add"
            onClick={addCondition}
          >
            + Add condition
          </button>
        )}
      </div>

      <div className="ffm-footer">
        <button
          type="button"
          className="ffm-cancel"
          data-testid="field-filter-cancel"
          onClick={onClose}
        >
          Cancel
        </button>
        <button
          type="button"
          className="ffm-apply"
          data-testid="field-filter-apply"
          onClick={handleApply}
        >
          Apply
        </button>
      </div>

      {/* Single-select, used exactly as the record modal uses it: '' means
          unset, a row tap hands back null for "— none —" or the option string,
          and THE PARENT COMMITS THEN CLOSES (BottomSheetSelect.jsx:15). Close
          here is the host's navigate(-1), so every dismissal path — row tap,
          backdrop, drag-down, Escape, hardware back — resolves through the one
          pop, exactly as tmgFieldSheetField does in the record modal.

          isOpen derives from `sheetCondition`, not from the raw index, which
          also covers a stale sentinel: a forward navigation could restore an
          index past the end of a freshly-remounted draft, and draft[i] being
          undefined simply reads as closed rather than throwing. */}
      <BottomSheetSelect
        isOpen={sheetCondition != null}
        label={fields.find((f) => f.key === sheetCondition?.fieldKey)?.label || 'Value'}
        value={sheetCondition?.value ?? ''}
        options={sheetCondition ? getFieldUniqueValues(records, sheetCondition.fieldKey) : []}
        onSelect={(next) => {
          setCondition(sheetIndex, { value: next ?? '' });
          onSheetClose();
        }}
        onClose={onSheetClose}
      />
    </div>
  );
}
